import { useTranslation } from 'react-i18next';
import { formatPhoneNumber } from '~/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { REGISTER_META_TOKEN } from '~/services/meta';
import WhatsAppIcon from '~/assets/icons/WhatsAppIcon';
import InstagramIcon from '~/assets/icons/InstagramIcon';
import { Background } from './Background';
import { Business } from '../types';
import {
  handleConnectWhatsapp,
  handleConnectInstagram,
} from '../../integrations/integration-handlers';

interface FinalOnboardingProps {
  agentId: string | undefined;
  business: Partial<Business>;
}

export const FinalOnboarding = ({ agentId, business }: FinalOnboardingProps) => {
  const [t] = useTranslation('onboarding');
  const navigate = useNavigate();
  const [registerToken] = useMutation(REGISTER_META_TOKEN);
  const refetch = () => {}; // No-op, or implement if needed

  const whatsappRequirements = t('integration_cards.whatsapp.requirements', {
    returnObjects: true,
  });
  const whatsappReqList = Array.isArray(whatsappRequirements)
    ? whatsappRequirements
    : [];
  const instagramRequirements = t('integration_cards.instagram.requirements', {
    returnObjects: true,
  });
  const instagramReqList = Array.isArray(instagramRequirements)
    ? instagramRequirements
    : [];

  return (
    <Background fullScreen>
      <div className="flex flex-col items-center justify-center gap-4 px-4 pb-4 pt-20 text-center overflow-y-auto touch-auto max-h-screen md:px-0 md:pb-0 md:pt-10">
        <h1 className="mb-6 text-3xl font-bold md:text-5xl">
          {t('ready_title')}
        </h1>
        <p className="w-full text-center md:w-3/5">
          {t('ready_description', {
            whatsapp: formatPhoneNumber(business.whatsapp?.channel_id),
          })}
        </p>
        <div className="mb-2 w-full text-center text-base font-medium text-base-content md:w-3/5">
          {t('connect_prompt')}
        </div>
        <div className="mb-8 flex flex-col justify-center gap-6 md:flex-row">
          {/* WhatsApp Card */}
          <div className="flex w-64 flex-col justify-between rounded-lg border border-base-300 bg-base-100 p-4 text-left shadow-sm">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <WhatsAppIcon className="h-6 w-6" />
                <h3 className="text-md font-semibold text-base-content">
                  {t('integration_cards.whatsapp.title')}
                </h3>
              </div>
              <p className="mb-2 mt-1 flex-grow text-xs text-base-content">
                {t('integration_cards.whatsapp.description')}
              </p>
              <ul className="mb-4 list-inside list-disc pl-2 text-xs text-base-content">
                {whatsappReqList.map((item: string, idx: number) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
            <button
              className="btn btn-primary mt-auto w-full rounded-full rounded-md px-3 py-2 text-sm font-black focus:outline-none focus:ring-2"
              onClick={() =>
                handleConnectWhatsapp(
                  agentId || business?.agent_id,
                  registerToken,
                  navigate,
                  refetch,
                )
              }
            >
              {t('integration_cards.whatsapp.button')}
            </button>
          </div>
          {/* Instagram Card */}
          <div className="flex w-64 flex-col justify-between rounded-lg border border-base-300 bg-base-100 p-4 text-left shadow-sm">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <InstagramIcon className="h-6 w-6" />
                <h3 className="text-md font-semibold text-base-content">
                  {t('integration_cards.instagram.title')}
                </h3>
              </div>
              <p className="mb-2 mt-1 flex-grow text-xs text-base-content">
                {t('integration_cards.instagram.description')}
              </p>
              <ul className="mb-4 list-inside list-disc pl-2 text-xs text-base-content">
                {instagramReqList.map((item: string, idx: number) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
            <button
              className="btn btn-secondary mt-auto w-full rounded-md px-3 py-2 text-sm font-black focus:outline-none focus:ring-2"
              onClick={() =>
                handleConnectInstagram(
                  agentId || business?.agent_id,
                )
              }
            >
              {t('integration_cards.instagram.button')}
            </button>
          </div>
        </div>
        <a
          href={agentId 
            ? `/agent/${agentId}/dashboard` 
            : '/'
          }
          className="mx-auto block text-xs text-base-content underline transition hover:text-base-content"
        >
          {t('skip')}
        </a>
      </div>
    </Background>
  );
};
